/**
 * Lijsten Beheren Menu (Epic C-2 — Kwalificaties, Story C-2.1, UX-C3)
 *
 * HR-only beheer-UI voor de twee kwalificatie-stamdata-lijsten
 * (productcategorieën/doelmarkten): toevoegen, hernoemen, archiveren,
 * herstellen. Zelfde custom-modal-overlay + Escape-to-close-patroon als
 * `TaakFormModal.tsx`/`UitDienstDialog.tsx` — geen shadcn/ui.
 *
 * Story D-1.3 (Epic D-1 — dagplanning) voegt een derde segment toe:
 * Capaciteit (`CapaciteitBeheerSectie`), via een eigen `BeheerTab`-union —
 * `QualificationListKind` blijft de twee lijstwaarden houden.
 *
 * Self-gated op `hasRole(['admin','hr'])` (AC 4: "niet-HR-gebruikers zien
 * deze ingang niet") zodat dit component standalone (geïsoleerd, vóórdat de
 * volle Kwalificaties-tab in Story C-2.2 landt) al zijn eigen zichtbaarheids-
 * regel afdwingt — de host-pagina hoeft niet apart te gaten.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useToast } from '../../../components/Toast';
import {
  useQualificationListItems,
  useCreateListItem,
  useUpdateListItem,
  useArchiveListItem,
  useRestoreListItem,
} from '../hooks/useQualificationLists';
import { SegmentedControl, type SegmentedControlOption } from './SegmentedControl';
import { CapaciteitBeheerSectie } from './CapaciteitBeheerSectie';
import { ApiRequestError } from '../../../lib/api';
import type { QualificationListKind } from '../../../lib/qualificationsApi';
import type { QualificationListItemDTO } from '@functieloonhuis/shared';

/**
 * Story D-1.3: eigen union voor de segmenten van dit menu. Bewust NIET
 * `QualificationListKind` verbreed — dat type is een URL-padsegment van de
 * kwalificatie-endpoints; een derde waarde erin levert ergens een
 * `/api/v1/qualifications/capaciteit`-aanroep op. `ListItemsSection` krijgt
 * alleen de twee lijstwaarden door.
 */
type BeheerTab = QualificationListKind | 'capaciteit';

const BEHEER_TAB_OPTIONS: readonly SegmentedControlOption<BeheerTab>[] = [
  { value: 'product-categories', label: 'Productcategorieën' },
  { value: 'target-markets', label: 'Doelmarkten' },
  { value: 'capaciteit', label: 'Capaciteit' },
];

const NAME_TAKEN_MESSAGE = 'Er bestaat al een actief item met deze naam.';

function ListItemsSection({ kind }: { kind: QualificationListKind }) {
  const [showArchived, setShowArchived] = useState(false);
  const { data: items, isLoading } = useQualificationListItems(kind, showArchived);
  const createItem = useCreateListItem(kind);
  const updateItem = useUpdateListItem(kind);
  const archiveItem = useArchiveListItem(kind);
  const restoreItem = useRestoreListItem(kind);
  const { showToast } = useToast();

  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  async function handleAdd() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setAddError(null);
    try {
      await createItem.mutateAsync({ name: trimmed });
      setNewName('');
      showToast('Item toegevoegd', 'success');
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === 'LIST_NAME_TAKEN') {
        setAddError(NAME_TAKEN_MESSAGE);
      } else {
        showToast('Toevoegen is niet gelukt', 'error');
      }
    }
  }

  function startEdit(item: QualificationListItemDTO) {
    setEditingId(item.id);
    setEditingName(item.name);
    setEditError(null);
  }

  async function handleSaveEdit(id: number) {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    setEditError(null);
    try {
      await updateItem.mutateAsync({ id, data: { name: trimmed } });
      setEditingId(null);
      showToast('Item hernoemd', 'success');
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === 'LIST_NAME_TAKEN') {
        setEditError(NAME_TAKEN_MESSAGE);
      } else {
        showToast('Hernoemen is niet gelukt', 'error');
      }
    }
  }

  async function handleArchive(id: number) {
    try {
      await archiveItem.mutateAsync(id);
    } catch {
      showToast('Archiveren is niet gelukt', 'error');
    }
  }

  async function handleRestore(id: number) {
    try {
      await restoreItem.mutateAsync(id);
    } catch (error) {
      const message =
        error instanceof ApiRequestError && error.code === 'LIST_NAME_TAKEN'
          ? 'Er bestaat al een actief item met deze naam — herstellen niet mogelijk.'
          : 'Herstellen is niet gelukt';
      showToast(message, 'error');
    }
  }

  const list = items ?? [];
  const showEmptyState = !isLoading && list.length === 0;

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <input
          type="text"
          data-testid="lijst-item-naam-input"
          value={newName}
          onChange={(e) => {
            setNewName(e.target.value);
            setAddError(null);
          }}
          placeholder="Naam nieuw item"
          aria-invalid={!!addError}
          aria-describedby={addError ? 'lijst-item-naam-conflict-error' : undefined}
          className="flex-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
        />
        <button
          type="button"
          data-testid="lijst-item-toevoegen-button"
          onClick={handleAdd}
          disabled={createItem.isPending || !newName.trim()}
          className="rounded-md bg-[#2F5A7A] px-3 py-2 text-sm font-medium text-white hover:bg-[#2F5A7A]/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          + Toevoegen
        </button>
      </div>
      {addError && (
        <p id="lijst-item-naam-conflict-error" data-testid="lijst-item-naam-conflict-error" className="-mt-2 mb-3 text-xs text-[#D64545]">
          {addError}
        </p>
      )}

      <label className="mb-3 flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          data-testid="lijsten-beheren-toon-gearchiveerd-toggle"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
          className="rounded border-[#E2E8F0] text-[#54949E] focus:ring-[#54949E]"
        />
        Toon gearchiveerd
      </label>

      {showEmptyState && (
        <p data-testid="lijsten-beheren-lege-staat" className="py-6 text-center text-sm text-gray-500">
          Nog geen items — voeg de eerste toe.
        </p>
      )}

      {!showEmptyState && (
        <ul className="divide-y divide-[#E2E8F0]">
          {list.map((item) => (
            <li key={item.id} data-testid={`lijst-item-rij-${item.id}`} className="flex items-center justify-between gap-2 py-2">
              {editingId === item.id ? (
                <>
                  <input
                    type="text"
                    data-testid={`lijst-item-hernoemen-input-${item.id}`}
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="flex-1 rounded-md border border-[#E2E8F0] px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
                  />
                  {editError && <span className="text-xs text-[#D64545]">{editError}</span>}
                  <button
                    type="button"
                    data-testid={`lijst-item-hernoemen-opslaan-${item.id}`}
                    onClick={() => handleSaveEdit(item.id)}
                    disabled={updateItem.isPending}
                    className="text-sm font-medium text-[#54949E] hover:underline"
                  >
                    Opslaan
                  </button>
                  <button type="button" onClick={() => setEditingId(null)} className="text-sm text-gray-600 hover:underline">
                    Annuleren
                  </button>
                </>
              ) : (
                <>
                  <span className={item.isArchived ? 'text-gray-400' : 'text-[#1E293B]'}>
                    {item.name}
                    {item.isArchived && ' (gearchiveerd)'}
                  </span>
                  <span className="flex gap-3">
                    {item.isArchived ? (
                      <button
                        type="button"
                        data-testid={`lijst-item-herstellen-${item.id}`}
                        onClick={() => handleRestore(item.id)}
                        disabled={restoreItem.isPending}
                        className="text-sm font-medium text-[#54949E] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Herstellen
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          data-testid={`lijst-item-hernoemen-${item.id}`}
                          onClick={() => startEdit(item)}
                          className="text-sm font-medium text-[#54949E] hover:underline"
                        >
                          Hernoemen
                        </button>
                        <button
                          type="button"
                          data-testid={`lijst-item-archiveren-${item.id}`}
                          onClick={() => handleArchive(item.id)}
                          disabled={archiveItem.isPending}
                          className="text-sm font-medium text-gray-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Archiveren
                        </button>
                      </>
                    )}
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function LijstenBeherenMenu() {
  const { hasRole } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<BeheerTab>('product-categories');

  useEffect(() => {
    if (!isOpen) return;
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [isOpen]);

  if (!hasRole(['admin', 'hr'])) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        data-testid="lijsten-beheren-button"
        onClick={() => setIsOpen(true)}
        className="rounded-md border border-[#E2E8F0] px-3 py-2 text-sm font-medium text-[#2F5A7A] hover:bg-gray-50"
      >
        Lijsten beheren
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="lijsten-beheren-overlay">
          <div
            role="dialog"
            aria-modal="true"
            data-testid="lijsten-beheren-dialog"
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Lijsten beheren</h2>
              <button type="button" onClick={() => setIsOpen(false)} className="text-sm text-gray-500 hover:underline" aria-label="Sluiten">
                Sluiten
              </button>
            </div>

            <div className="mb-4">
              <SegmentedControl options={BEHEER_TAB_OPTIONS} value={activeTab} onChange={setActiveTab} ariaLabel="Kies onderdeel" />
            </div>

            {activeTab === 'capaciteit' ? <CapaciteitBeheerSectie /> : <ListItemsSection kind={activeTab} />}
          </div>
        </div>
      )}
    </>
  );
}
